// Paywall plumbing on the client. While /api/config says paywall: false
// (today), nothing here is visible and every pack is open.

import { useEffect, useState } from 'react';
import { playerId, save, loadRaw } from '../lib/storage';

export interface SiteConfig {
  paywall: boolean;
  freePacks: string[];
  priceLabel: string;
  checkout: boolean;
  blocklist: string;
  judgeFreePerDay: number | null;
}

export const DEFAULT_CONFIG: SiteConfig = {
  paywall: false,
  freePacks: [],
  priceLabel: '',
  checkout: false,
  blocklist: '',
  judgeFreePerDay: null,
};

export async function fetchConfig(): Promise<SiteConfig> {
  try {
    const r = await fetch('/api/config');
    if (!r.ok) return DEFAULT_CONFIG;
    return { ...DEFAULT_CONFIG, ...((await r.json()) as Partial<SiteConfig>) };
  } catch {
    return DEFAULT_CONFIG;
  }
}

export interface ProState {
  pro: boolean;
  code: string | null;
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const r = await fetch(url, init);
    return r.ok ? ((await r.json()) as T) : null;
  } catch {
    return null;
  }
}

export async function fetchEntitlement(): Promise<ProState> {
  const j = await getJson<{ pro: boolean; code?: string }>(`/api/entitlement?pid=${playerId()}`);
  return { pro: !!j?.pro, code: j?.code ?? loadRaw('code') };
}

export async function startCheckout(): Promise<boolean> {
  const j = await getJson<{ url: string }>('/api/checkout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ pid: playerId() }),
  });
  if (!j?.url) return false;
  window.location.href = j.url;
  return true;
}

/// After Stripe sends the buyer back (?checkout=success&session_id=…).
export async function confirmFromUrl(): Promise<ProState | null> {
  const u = new URL(window.location.href);
  if (u.searchParams.get('checkout') !== 'success') return null;
  const sid = u.searchParams.get('session_id') ?? '';
  u.searchParams.delete('checkout');
  u.searchParams.delete('session_id');
  window.history.replaceState(null, '', u.toString());
  const j = await getJson<{ pro: boolean; code?: string }>(
    `/api/checkout/confirm?session_id=${encodeURIComponent(sid)}&pid=${playerId()}`,
  );
  if (j?.code) save('code', j.code);
  return j ? { pro: j.pro, code: j.code ?? null } : null;
}

export async function restoreCode(code: string): Promise<ProState> {
  const j = await getJson<{ pro: boolean; code?: string }>('/api/restore', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ pid: playerId(), code }),
  });
  if (j?.code) save('code', j.code);
  return { pro: !!j?.pro, code: j?.code ?? null };
}

/// Config and, with the paywall on, this browser's Pro state.
export function useSite() {
  const [config, setConfig] = useState<SiteConfig>(DEFAULT_CONFIG);
  const [pro, setPro] = useState<ProState>({ pro: false, code: null });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const c = await fetchConfig();
      if (!alive) return;
      setConfig(c);
      if (c.paywall && c.checkout) {
        const confirmed = await confirmFromUrl();
        const p = confirmed?.pro ? confirmed : await fetchEntitlement();
        if (alive) setPro(p);
      }
      if (alive) setLoaded(true);
    })();
    return () => {
      alive = false;
    };
  }, []);
  return { config, pro, setPro, loaded };
}

/// Whether [pack] is playable for this browser.
export const packOpen = (pack: string, config: SiteConfig, pro: ProState) =>
  !config.paywall || pro.pro || config.freePacks.includes(pack);
